import { useEffect } from 'react';

export const isEditableTarget = (target: EventTarget | null): boolean => {
	if (!(target instanceof HTMLElement)) return false;
	const tag = target.tagName;
	if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
	return target.isContentEditable;
};

export const isUnmodifiedEnter = (
	event: Pick<KeyboardEvent, 'key' | 'shiftKey' | 'ctrlKey' | 'metaKey' | 'altKey'>
): boolean =>
	event.key === 'Enter' && !event.shiftKey && !event.ctrlKey && !event.metaKey && !event.altKey;

interface UseConfirmOnEnterOptions {
	disabled?: boolean;
	onConfirm: () => void;
	shouldHandle?: (event: KeyboardEvent) => boolean;
}

/**
 * Confirm the active settings wizard step when Enter is pressed, unless focus is
 * inside an editable field. Mirrors Application navigation but skips inputs so
 * employee grid editing is not interrupted.
 */
export const useConfirmOnEnter = ({
	disabled = false,
	onConfirm,
	shouldHandle
}: UseConfirmOnEnterOptions): void => {
	useEffect(() => {
		if (disabled) return;

		const handleKeyDown = (event: KeyboardEvent): void => {
			if (!isUnmodifiedEnter(event)) return;
			if (isEditableTarget(event.target) && !shouldHandle?.(event)) return;

			event.preventDefault();
			onConfirm();
		};

		window.addEventListener('keydown', handleKeyDown);
		return () => window.removeEventListener('keydown', handleKeyDown);
	}, [disabled, onConfirm, shouldHandle]);
};
